/**
 * Enquiry types for the sales and tenant contact forms, plus the Zod schemas
 * the two forms validate with. The lists themselves live in the edge functions'
 * runtime-agnostic helper so the owner notification email uses the same labels.
 */
import { z } from 'zod';
import {
  SALES_ENQUIRY_TYPES,
  TENANT_ENQUIRY_TYPES,
  type SalesEnquiryType,
  type TenantEnquiryType,
} from '../../supabase/functions/_shared/enquiry-types';

export {
  NON_LEAD_ENQUIRY_TYPES,
  SALES_ENQUIRY_TYPES,
  TENANT_ENQUIRY_TYPES,
  countsAsLead,
  isNonLeadEnquiryType,
  isSalesEnquiryType,
  isTenantEnquiryType,
  ownerContactCopy,
  salesEnquirySubject,
  salesEnquiryTypeLabel,
  tenantEnquiryTypeLabel,
  tenantEnquiryTypeShortLabel,
  type SalesEnquiryType,
  type TenantEnquiryType,
} from '../../supabase/functions/_shared/enquiry-types';

const tenantValues = TENANT_ENQUIRY_TYPES.map((t) => t.value) as [TenantEnquiryType, ...TenantEnquiryType[]];
const salesValues = SALES_ENQUIRY_TYPES.map((t) => t.value) as [SalesEnquiryType, ...SalesEnquiryType[]];

/** Customer → cleaning business (tenant Contact page). */
export const tenantContactSchema = z.object({
  name: z.string().trim().min(1, 'Please tell us your name').max(200),
  email: z.string().trim().email('Please enter a valid email address').max(320),
  phone: z.string().trim().max(50).optional().or(z.literal('')),
  enquiryType: z.enum(tenantValues, {
    errorMap: () => ({ message: 'Please choose what your enquiry is about' }),
  }),
  message: z.string().trim().min(1, 'Please add a message').max(5000),
});
export type TenantContactValues = z.infer<typeof tenantContactSchema>;

/** Cleaning business owner → us (sales Contact page). */
export const salesContactSchema = z.object({
  name: z.string().trim().min(1, 'Please tell us your name').max(200),
  email: z.string().trim().email('Please enter a valid email address').max(320),
  business: z.string().trim().max(200).optional().or(z.literal('')),
  enquiryType: z.enum(salesValues, {
    errorMap: () => ({ message: "Please choose what it's about" }),
  }),
  message: z.string().trim().min(1, 'Please add a message').max(5000),
});
export type SalesContactValues = z.infer<typeof salesContactSchema>;
